import { useCallback, useEffect, useState } from 'react';

import type { OverrideBasis, OverrideOption, OverrideTier, OverridesValues } from './types';

const DEFAULT_VALUES: OverridesValues = {
  selectedOption: 1,
  basis: 'vendor_revenue',
  tiers: [{ to: 0, comms: 0 }],
  preferredOption: null,
};

// Local session state for the Overrides tab - mirrors useInclusionDrafts: in-memory
// `useState`, no persistence, reset to defaults when the offer changes. "Save Session"
// stays disabled until svc-offer draft persistence is wired (XTRNT-1073).
export function useOverrides(offerId: string | undefined) {
  const [overrides, setOverrides] = useState<OverridesValues>(DEFAULT_VALUES);

  useEffect(() => {
    setOverrides(DEFAULT_VALUES);
  }, [offerId]);

  const setOption = useCallback((selectedOption: OverrideOption) => {
    setOverrides((current) => ({ ...current, selectedOption }));
  }, []);

  const setPreferredOption = useCallback((preferredOption: OverrideOption | null) => {
    setOverrides((current) => ({ ...current, preferredOption }));
  }, []);

  const setBasis = useCallback((basis: OverrideBasis) => {
    setOverrides((current) => ({ ...current, basis }));
  }, []);

  const addTier = useCallback(() => {
    setOverrides((current) => ({ ...current, tiers: [...current.tiers, { to: 0, comms: 0 }] }));
  }, []);

  const updateTier = useCallback((index: number, changes: Partial<OverrideTier>) => {
    setOverrides((current) => ({
      ...current,
      tiers: current.tiers.map((tier, tierIndex) => (tierIndex === index ? { ...tier, ...changes } : tier)),
    }));
  }, []);

  const removeTier = useCallback((index: number) => {
    setOverrides((current) => ({
      ...current,
      tiers: current.tiers.filter((_, tierIndex) => tierIndex !== index),
    }));
  }, []);

  return { overrides, setOption, setPreferredOption, setBasis, addTier, updateTier, removeTier };
}
